import Component from '@glimmer/component';
import PageSetup from 'bcl/components/page-setup';
import pageTitle from 'ember-page-title/helpers/page-title';
import OrderForm from 'bcl/components/order-form';
import type OrderModel from 'bcl/models/order';
import { service } from '@ember/service';
import type SessionService from 'bcl/services/session';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import type LocationModel from 'bcl/models/location';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { acceptOrder } from './accept';
import type OrderEntryModel from 'bcl/models/order-entry';

export interface OrdersNewSignature {
  // The arguments accepted by the component
  Args: {
    model: OrderModel;
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class OrdersNewComponent extends Component<OrdersNewSignature> {
  @service declare session: SessionService;
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked location?: LocationModel;
  @action changeLocation(location: LocationModel) {
    this.location = location;
  }

  @action
  async cancel() {
    await this.args.model.destroyRecord();
    this.router.transitionTo('auth.orders');
  }

  @tracked isSaving = false;
  @action
  async save() {
    this.isSaving = true;
    await saveOrderAndEntries(
      this.args.model,
      this.store,
      this.router,
      this.session,
      this.location
    );
    this.isSaving = false;
  }

  <template>
    {{pageTitle "Neue Bestellung"}}
    <PageSetup @title="Neue Bestellung">
      <OrderForm
        @model={{@model}}
        @save={{this.save}}
        @cancel={{this.cancel}}
        @location={{this.location}}
        @changeLocation={{this.changeLocation}}
        @isSaving={{this.isSaving}}
      />
    </PageSetup>
  </template>
}
export async function saveOrderAndEntries(
  model: OrderModel,
  store: Store,
  router: RouterService,
  session: SessionService,
  location: LocationModel
) {
  const oe = model.orderEntries as OrderEntryModel[];
  const orderEntriesKeep = oe.map((entry) => {
    if (entry.amount === 0 || entry.amount === '0') {
      return false;
    }

    return true;
  });
  const orderEntries = oe.filter((item, idx) => {
    return orderEntriesKeep[idx];
  });
  oe.filter((item, idx) => {
    // console.log(idx, orderEntriesKeep[idx]);
    return !orderEntriesKeep[idx];
  }).forEach((item) => item.deleteRecord());

  orderEntries.sort((a, b) =>
    (a.supplier_item.item.name ?? '').localeCompare(
      b.supplier_item.item.name ?? '',
      'de'
    )
  );

  orderEntries.forEach((entry: OrderEntryModel, index) => {
    entry.position = index + 1;
    entry.amount_delivered = entry.amount;
  });
  model.status = 'created';

  try {
    if (model.orderType === 'special') {
      await model.save();
      await acceptOrder(
        model,
        location,
        store.peekRecord('user', session.user_id),
        store
      );
      router.transitionTo('auth.orders');
      return;
    }
    await model.save();
    const promises = [];
    const toSaveOe = model.orderEntries as OrderEntryModel[];
    for (let i = 0; i < toSaveOe.length; i++) {
      promises.push(toSaveOe[i].save());
    }
    await Promise.all(promises);
    router.transitionTo('auth.orders');
  } catch (e) {
    console.error(e);
    alert(
      'Beim speichern ist ein Fehler aufgetreten. Der Fehler wurde gemeldet. Bitte versuch es erneut.'
    );
  }
}
